import React, { Component } from 'react';
import { titleize } from 'underscore.string';
import { t } from 'i18next';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cargomatic/ui';
import { FilterDatePicker } from 'components/design-system/molecules/FilterDatePicker';
import {
  DATE_FILTER_SELECTIONS,
  DAYS_AGO_OR_OLDER_TYPE,
  MAX_DAYS_AGO,
} from '../constants';

// Custom AG Grid date filter that replaces the built-in agDateColumnFilter so it can
// host a free-form "X+ Days Ago" numeric input (the built-in cannot). It reproduces the
// built-in's option set + multi-condition AND/OR behavior, and emits the same model
// shapes the server-side query pipeline (transformers.js) already understands.

const ZERO_INPUT_OPS = new Set(['blank', 'notBlank']);
const RELATIVE_SLUGS = new Set(
  DATE_FILTER_SELECTIONS.map((item) => item.localizationSlug),
);

const BASE_OPTIONS = [
  { value: 'equals', name: 'Equals' },
  { value: 'notEqual', name: 'Not equal' },
  { value: 'before', name: 'Before' },
  { value: 'after', name: 'After' },
  { value: 'inRange', name: 'Between' },
  { value: DAYS_AGO_OR_OLDER_TYPE, name: 'X+ Days Ago' },
  { value: 'blank', name: 'Blank' },
  { value: 'notBlank', name: 'Not blank' },
];

const OPTIONS = [
  ...BASE_OPTIONS,
  ...DATE_FILTER_SELECTIONS.map((item) => ({
    value: item.localizationSlug,
    name: item.name,
  })),
];

const emptyCondition = () => ({
  op: 'equals',
  from: null,
  to: null,
  daysAgo: '',
});

const getDefaultState = () => ({
  conditions: [emptyCondition()],
  operator: 'AND',
});

const toIso = (date) => (date ? new Date(date).toISOString() : null);

const isDaysAgoValid = (value) => {
  const days = Number(value);
  return Number.isInteger(days) && days >= 1 && days <= MAX_DAYS_AGO;
};

const isConditionActive = (condition) => {
  const { op } = condition;
  if (!op) return false;
  if (ZERO_INPUT_OPS.has(op) || RELATIVE_SLUGS.has(op)) return true;
  if (op === DAYS_AGO_OR_OLDER_TYPE) return isDaysAgoValid(condition.daysAgo);
  if (op === 'inRange') return Boolean(condition.from && condition.to);
  return Boolean(condition.from);
};

const conditionToModel = (condition) => {
  const { op } = condition;
  if (ZERO_INPUT_OPS.has(op)) return { filterType: 'date', type: op };
  if (RELATIVE_SLUGS.has(op)) {
    return { filterType: 'date', type: op, dateFrom: null, dateTo: null };
  }
  if (op === DAYS_AGO_OR_OLDER_TYPE) {
    return {
      filterType: 'date',
      type: DAYS_AGO_OR_OLDER_TYPE,
      daysAgo: Number(condition.daysAgo),
    };
  }
  if (op === 'inRange') {
    return {
      filterType: 'date',
      type: 'inRange',
      dateFrom: toIso(condition.from),
      dateTo: toIso(condition.to),
    };
  }
  return {
    filterType: 'date',
    type: op,
    dateFrom: toIso(condition.from),
    dateTo: null,
  };
};

const modelToCondition = (model = {}) => ({
  op: model.type || 'equals',
  from: model.dateFrom ? new Date(model.dateFrom) : null,
  to: model.dateTo ? new Date(model.dateTo) : null,
  daysAgo: model.daysAgo ?? '',
});

class DateFilter extends Component {
  constructor(props) {
    super(props);
    this.state = getDefaultState();
  }

  getMaxConditions() {
    return this.props.maxNumConditions || 10;
  }

  getMinDate() {
    return this.props.minValidDate
      ? new Date(this.props.minValidDate)
      : undefined;
  }

  getModel() {
    const { conditions, operator } = this.state;
    const active = conditions.filter(isConditionActive);
    if (!active.length) return null;

    const models = active.map(conditionToModel);
    if (models.length === 1) return models[0];

    return {
      filterType: 'date',
      operator,
      conditions: models,
      condition1: models[0],
      condition2: models[1],
    };
  }

  setModel(model) {
    if (!model || Object.keys(model).length === 0) {
      this.setState(getDefaultState());
      return;
    }

    let list;
    if (Array.isArray(model.conditions) && model.conditions.length) {
      list = model.conditions;
    } else if (model.condition1) {
      list = [model.condition1, model.condition2].filter(Boolean);
    } else {
      list = [model];
    }

    this.setState({
      conditions: list.map(modelToCondition),
      operator: model.operator || 'AND',
    });
  }

  isFilterActive() {
    return this.state.conditions.some(isConditionActive);
  }

  // Filtering is performed server-side; AG Grid must not re-filter client rows.
  doesFilterPass() {
    return true;
  }

  getModelAsString() {
    const model = this.getModel();
    if (!model) return '';
    const describe = (condition) => {
      const option = OPTIONS.find((opt) => opt.value === condition.type);
      const label = option ? option.name : condition.type;
      if (condition.type === DAYS_AGO_OR_OLDER_TYPE) {
        return `${condition.daysAgo}+ days ago`;
      }
      if (condition.type === 'inRange') {
        return `${label}: ${condition.dateFrom || ''} – ${condition.dateTo || ''}`;
      }
      if (condition.dateFrom) return `${label}: ${condition.dateFrom}`;
      return label;
    };
    const list = model.conditions || [model];
    return list.map(describe).join(` ${model.operator || ''} `);
  }

  updateCondition(index, patch) {
    this.setState((prev) => {
      const conditions = prev.conditions.map((condition, i) =>
        i === index ? { ...condition, ...patch } : condition,
      );
      return { conditions };
    });
  }

  handleOptionChange(index, value) {
    this.updateCondition(index, {
      op: value,
      from: null,
      to: null,
      daysAgo: '',
    });
  }

  addCondition = () => {
    this.setState((prev) => {
      if (prev.conditions.length >= this.getMaxConditions()) return prev;
      return { conditions: [...prev.conditions, emptyCondition()] };
    });
  };

  removeCondition = (index) => {
    this.setState((prev) => {
      const conditions = prev.conditions.filter((_, i) => i !== index);
      return {
        conditions: conditions.length ? conditions : [emptyCondition()],
      };
    });
  };

  toggleOperator = () => {
    this.setState((prev) => ({
      operator: prev.operator === 'AND' ? 'OR' : 'AND',
    }));
  };

  handleApply = () => {
    this.props.filterChangedCallback();
  };

  handleClear = () => {
    this.setState(getDefaultState(), () => {
      this.props.filterChangedCallback();
    });
  };

  renderInputs(condition, index) {
    const { op } = condition;
    if (ZERO_INPUT_OPS.has(op) || RELATIVE_SLUGS.has(op)) return null;

    if (op === DAYS_AGO_OR_OLDER_TYPE) {
      return (
        <div className="flex items-center gap-2 mt-2">
          <Input
            type="number"
            min={1}
            max={MAX_DAYS_AGO}
            step={1}
            className="w-24"
            value={condition.daysAgo}
            onChange={(event) =>
              this.updateCondition(index, { daysAgo: event.target.value })
            }
          />
          <span className="text-sm text-muted-foreground">
            days ago or older
          </span>
        </div>
      );
    }

    if (op === 'inRange') {
      return (
        <div className="flex flex-col gap-2 mt-2">
          <FilterDatePicker
            value={condition.from}
            minDate={this.getMinDate()}
            onChange={(date) =>
              this.updateCondition(index, { from: date || null })
            }
            placeholder="From"
          />
          <FilterDatePicker
            value={condition.to}
            minDate={this.getMinDate()}
            onChange={(date) =>
              this.updateCondition(index, { to: date || null })
            }
            placeholder="To"
          />
        </div>
      );
    }

    return (
      <div className="mt-2">
        <FilterDatePicker
          value={condition.from}
          minDate={this.getMinDate()}
          onChange={(date) =>
            this.updateCondition(index, { from: date || null })
          }
        />
      </div>
    );
  }

  render() {
    const { conditions, operator } = this.state;
    return (
      <div className="px-4 py-4 w-72">
        {conditions.map((condition, index) => (
          <div key={index}>
            {index > 0 && (
              <div className="flex justify-center my-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={this.toggleOperator}
                >
                  {operator}
                </Button>
              </div>
            )}
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Select
                  value={condition.op}
                  onValueChange={(value) =>
                    this.handleOptionChange(index, value)
                  }
                >
                  <SelectTrigger className="flex-1 h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[1100] ag-custom-component-popup">
                    {OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {conditions.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => this.removeCondition(index)}
                  >
                    ✕
                  </Button>
                )}
              </div>
              {this.renderInputs(condition, index)}
            </div>
          </div>
        ))}

        {conditions.length < this.getMaxConditions() && (
          <div className="mt-3">
            <Button variant="outline" size="sm" onClick={this.addCondition}>
              {titleize(t('ADD'))}
            </Button>
          </div>
        )}

        <div className="flex justify-between mt-4">
          <Button variant="outline" onClick={this.handleClear}>
            {titleize(t('CLEAR'))}
          </Button>
          <Button onClick={this.handleApply}>{titleize(t('APPLY'))}</Button>
        </div>
      </div>
    );
  }
}

export default DateFilter;
